import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { IconTouchable } from '../../util/iconTouchable';

/**
 * A dismissable bubble hung under the control that raised it.
 *
 *   <div className="relative">
 *     <Button onClick={submit}>Do košíku</Button>
 *     <AnchoredAlert variant={AnchoredAlertVariant.DESTRUCTIVE}>
 *       <AnchoredAlertMessage>{message}</AnchoredAlertMessage>
 *       <AnchoredAlertClose onClick={dismiss} aria-label={close} />
 *     </AnchoredAlert>
 *   </div>
 *
 * A primitive rather than a section part: what it says is always about
 * commerce, but what it IS — an absolutely positioned bubble with a message
 * and a cross — carries no commerce meaning at all, which is the whole bar for
 * this tier. Its two callers today are an add-to-cart control on a product card
 * and one inside a CMS content block; the name says neither, so the next
 * caller does not have to be a cart.
 *
 * It anchors against the nearest positioned ancestor, which the composition
 * owns: this part cannot make its own trigger `relative` without wrapping it.
 */
export const anchoredAlertVariants = cva(
  'absolute top-[calc(100%+0.5rem)] right-0 z-10 flex items-start gap-4 rounded-xl p-3 shadow-lg',
  {
    variants: {
      variant: {
        /** Something failed. */
        destructive: 'bg-destructive text-destructive-foreground',
        /** A limit the person has reached. */
        warning: 'bg-warning text-warning-foreground',
        /** The opt-out: a caller painting the bubble itself. */
        unstyled: '',
      },
    },
  }
);

type AnchoredAlertVariantName = NonNullable<VariantProps<typeof anchoredAlertVariants>['variant']>;

export type AnchoredAlertVariant = AnchoredAlertVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AnchoredAlertVariant = {
  DESTRUCTIVE: 'destructive',
  WARNING: 'warning',
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, AnchoredAlertVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AnchoredAlertVariantProps = { readonly variant: AnchoredAlertVariant };

export type AnchoredAlertProps = ComponentProps<'div'> & AnchoredAlertVariantProps;

/** The bubble. */
export const AnchoredAlert = ({ className, variant, ...props }: AnchoredAlertProps) => (
  <div
    data-slot="anchored-alert"
    className={clsxm(anchoredAlertVariants({ variant }), className)}
    {...props}
  />
);

/** The message, taking the room the close control leaves. */
const anchoredAlertMessageClasses = 'flex-1 tracking-tight';

export type AnchoredAlertMessageProps = ComponentProps<'span'>;

/** What the bubble says. */
export const AnchoredAlertMessage = ({ className, ...props }: AnchoredAlertMessageProps) => (
  <span
    data-slot="anchored-alert-message"
    className={clsxm(anchoredAlertMessageClasses, className)}
    {...props}
  />
);

/**
 * What dismisses the bubble.
 *
 * It renders its own cross rather than taking one as children: the glyph is
 * this part's answer to "how is this dismissed", and every caller's answer is
 * the same cross. The label for it is not — that is copy, so it arrives as
 * `aria-label` from the caller.
 */
const anchoredAlertCloseClasses = 'size-5 shrink-0';

export type AnchoredAlertCloseProps = Omit<
  ComponentProps<typeof IconTouchable<'button'>>,
  'as' | 'children'
>;

/** The bubble's dismiss control. */
export const AnchoredAlertClose = ({ className, ...props }: AnchoredAlertCloseProps) => (
  <IconTouchable type="button" data-slot="anchored-alert-close" className={className} {...props}>
    <X data-slot="anchored-alert-close-icon" aria-hidden className={anchoredAlertCloseClasses} />
  </IconTouchable>
);
